import {
    type FC,
    useId
} from "react";
import type IColorSliderRampProps from "./type";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    LinearGradient,
    Pattern,
    Defs,
    Stop,
    Rect,
    Svg
} from "react-native-svg";
import {
    getColorSliderPosition,
    getColorSliderVector,
    getColorSliderRect
} from "../../util";

const ColorSliderRamp: FC<IColorSliderRampProps> = ({
    trackThickness,
    customTheme,
    orientation,
    direction,
    thumbSize,
    channel,
    length,
    ramp
}) => {
    const {
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const idPrefix = useId().replace(/[^a-zA-Z0-9]/g, "");

    const gradientID = `${idPrefix}-gradient`;

    const checkerID = `${idPrefix}-checker`;

    const hatchID = `${idPrefix}-hatch`;

    if(length <= 0) {
        return null;
    }

    const isVertical = orientation === "vertical";

    const vector = getColorSliderVector({
        orientation,
        direction
    });

    const extent = ramp.limit === null ? length : getColorSliderPosition({
        fraction: ramp.limit,
        thumbSize,
        length
    });

    const rect = getColorSliderRect({
        thickness: trackThickness,
        orientation,
        direction,
        length,
        extent
    });

    const cell = spaces.spacingXs;

    const period = cell + cell;

    const hatchPeriod = spaces.spacingSm;

    const renderStops = () => {
        return ramp.stops.map((stop, index) => <Stop
            offset={getColorSliderPosition({
                fraction: stop.fraction,
                thumbSize,
                length
            }) / extent}
            stopOpacity={stop.opacity}
            stopColor={stop.color}
            key={index}
        />);
    };

    const renderBackdrop = () => {
        if(channel === "alpha") {
            return <>
                <Defs>
                    <Pattern
                        patternUnits="userSpaceOnUse"
                        height={period}
                        width={period}
                        id={checkerID}
                    >
                        <Rect
                            fill={colors.content.container.raised}
                            height={period}
                            width={period}
                        />
                        <Rect
                            fill={colors.content.border.default}
                            height={cell}
                            width={cell}
                        />
                        <Rect
                            fill={colors.content.border.default}
                            height={cell}
                            width={cell}
                            x={cell}
                            y={cell}
                        />
                    </Pattern>
                </Defs>
                <Rect
                    height={isVertical ? length : trackThickness}
                    width={isVertical ? trackThickness : length}
                    fill={`url(#${checkerID})`}
                />
            </>;
        }

        if(channel === "chroma") {
            return <>
                <Defs>
                    <Pattern
                        patternTransform="rotate(45)"
                        patternUnits="userSpaceOnUse"
                        height={hatchPeriod}
                        width={hatchPeriod}
                        id={hatchID}
                    >
                        <Rect
                            fill={colors.system.state.overlay.hover.default}
                            height={hatchPeriod}
                            width={borders.line}
                        />
                    </Pattern>
                </Defs>
                <Rect
                    height={isVertical ? length : trackThickness}
                    width={isVertical ? trackThickness : length}
                    fill={colors.content.container.subtle}
                />
                <Rect
                    height={isVertical ? length : trackThickness}
                    width={isVertical ? trackThickness : length}
                    fill={`url(#${hatchID})`}
                />
            </>;
        }

        return null;
    };

    return <Svg
        height={isVertical ? length : trackThickness}
        width={isVertical ? trackThickness : length}
    >
        {renderBackdrop()}
        <Defs>
            <LinearGradient
                id={gradientID}
                x1={vector.x1}
                x2={vector.x2}
                y1={vector.y1}
                y2={vector.y2}
            >
                {renderStops()}
            </LinearGradient>
        </Defs>
        <Rect
            fill={`url(#${gradientID})`}
            height={rect.height}
            width={rect.width}
            x={rect.x}
            y={rect.y}
        />
    </Svg>;
};
export default ColorSliderRamp;
